import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createReportPreset,
  deleteReportPreset,
  fetchReportPresets,
  updateReportPreset,
} from '../lib/apiClient';
import type { ReportPreset, ReportSchema } from '../types';

type FormState = {
  name: string;
  description: string;
  tags: string;
  schemaText: string;
  sampleReport: File | null;
  isDefault: boolean;
};

const initialFormState: FormState = {
  name: '',
  description: '',
  tags: '',
  schemaText: '',
  sampleReport: null,
  isDefault: false,
};

const formatSchema = (schema: ReportSchema): string => JSON.stringify(schema, null, 2);

export function ReportPresetsPage(): JSX.Element {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [formError, setFormError] = useState<string | null>(null);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const presetsQuery = useQuery({
    queryKey: ['report-presets'],
    queryFn: fetchReportPresets,
    staleTime: 1000 * 60 * 5,
  });

  const createMutation = useMutation({
    mutationFn: createReportPreset,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['report-presets'] });
      setForm(initialFormState);
      setFormError(null);
      setSchemaError(null);
    },
    onError: (error: unknown) => {
      setFormError(error instanceof Error ? error.message : 'Failed to create preset');
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: ({ presetId }: { presetId: string }) => updateReportPreset(presetId, { isDefault: true }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['report-presets'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteReportPreset,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['report-presets'] });
    },
  });

  const presets = useMemo<ReportPreset[]>(() => presetsQuery.data ?? [], [presetsQuery.data]);
  const defaultPresetId = presets.find((preset) => preset.isDefault)?._id;

  const handleChange = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const parseSchema = (): ReportSchema | undefined => {
    if (!form.schemaText.trim()) {
      return undefined;
    }
    try {
      return JSON.parse(form.schemaText) as ReportSchema;
    } catch (error) {
      setSchemaError('Schema must be valid JSON');
      return undefined;
    }
  };

  const parseTags = (): string[] | undefined => {
    if (!form.tags.trim()) {
      return [];
    }
    return form.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
  };

  const handleCreate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    setSchemaError(null);

    let schema: ReportSchema | undefined;
    if (form.schemaText.trim()) {
      schema = parseSchema();
      if (!schema) {
        return;
      }
    }

    const tags = parseTags();

    await createMutation.mutateAsync({
      name: form.name,
      description: form.description || undefined,
      schema,
      tags,
      sampleReport: form.sampleReport,
      isDefault: form.isDefault,
    });
  };

  const handleSetDefault = (preset: ReportPreset) => {
    setDefaultMutation.mutate({ presetId: preset._id });
  };

  const handleDelete = (preset: ReportPreset) => {
    if (preset.isDefault) {
      window.alert('Cannot delete the default preset. Set another preset as default first.');
      return;
    }
    if (window.confirm(`Delete preset “${preset.name}”? This cannot be undone.`)) {
      deleteMutation.mutate(preset._id);
    }
  };

  return (
    <div className="space-y-6">
      <header className="glass-card p-6">
        <h2 className="text-xl font-semibold text-foreground">Report presets</h2>
        <p className="text-sm text-muted-foreground">
          Upload sample reports or define JSON schemas to standardise how inspection outputs are rendered.
        </p>
      </header>

      <section className="glass-card p-6" data-onboarding="template-create">
        <form className="grid gap-4 md:grid-cols-2" onSubmit={handleCreate}>
          <div className="md:col-span-1 space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground" htmlFor="preset-name">
                Name
              </label>
              <input
                id="preset-name"
                type="text"
                required
                value={form.name}
                onChange={(event) => handleChange('name', event.target.value)}
                className="focus-ring w-full rounded-xl border border-border bg-white/70 px-3 py-2 text-sm text-foreground dark:bg-white/10"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground" htmlFor="preset-description">
                Description
              </label>
              <textarea
                id="preset-description"
                rows={3}
                value={form.description}
                onChange={(event) => handleChange('description', event.target.value)}
                className="focus-ring w-full rounded-xl border border-border bg-white/70 px-3 py-2 text-sm text-foreground dark:bg-white/10"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground" htmlFor="preset-tags">
                Tags
              </label>
              <input
                id="preset-tags"
                type="text"
                placeholder="comma,separated,tags"
                value={form.tags}
                onChange={(event) => handleChange('tags', event.target.value)}
                className="focus-ring w-full rounded-xl border border-border bg-white/70 px-3 py-2 text-sm text-foreground dark:bg-white/10"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground" htmlFor="preset-sample">
                Sample report (PDF)
              </label>
              <input
                id="preset-sample"
                type="file"
                accept="application/pdf"
                onChange={(event) => handleChange('sampleReport', event.target.files?.[0] ?? null)}
                className="focus-ring w-full rounded-xl border border-border bg-white/70 px-3 py-2 text-sm text-foreground file:mr-4 file:rounded-full file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-primary hover:file:bg-primary/20 dark:bg-white/10"
              />
              <p className="text-xs text-muted-foreground">
                Uploading a sample report will auto-generate the schema if no JSON is provided.
              </p>
            </div>
            <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              <input
                type="checkbox"
                checked={form.isDefault}
                onChange={(event) => handleChange('isDefault', event.target.checked)}
              />
              Set as default for new inspections
            </label>
            {formError && <p className="text-xs font-medium text-[hsl(var(--destructive))]">{formError}</p>}
          </div>
          <div className="md:col-span-1 space-y-3">
            <label className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground" htmlFor="preset-schema">
              Optional JSON schema
            </label>
            <textarea
              id="preset-schema"
              rows={14}
              placeholder="Paste a custom schema or leave blank to derive from sample report"
              value={form.schemaText}
              onChange={(event) => handleChange('schemaText', event.target.value)}
              className="focus-ring w-full rounded-xl border border-border bg-white/70 px-3 py-2 text-xs font-mono text-muted-foreground dark:bg-white/10"
            />
            {schemaError && <p className="text-xs font-medium text-[hsl(var(--destructive))]">{schemaError}</p>}
            <button
              type="submit"
              className="btn-glass btn-primary focus-ring text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating preset…' : 'Create preset'}
            </button>
          </div>
        </form>
      </section>

      <section className="space-y-4">
        {presetsQuery.isLoading && <div className="glass-card h-48 animate-pulse" />}

        {!presetsQuery.isLoading && presets.length === 0 && (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            <p className="font-semibold text-foreground">No report presets yet</p>
            <p className="mt-2">Upload a sample report or define a schema to get started.</p>
          </div>
        )}

        {!presetsQuery.isLoading && presets.length > 0 && (
          <div className="grid gap-4 lg:grid-cols-2">
            {presets.map((preset) => (
              <article key={preset._id} className="glass-card space-y-4 p-6">
                <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-foreground">{preset.name}</h3>
                      {preset.isDefault && (
                        <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
                          Default
                        </span>
                      )}
                    </div>
                    {preset.description && <p className="text-sm text-muted-foreground">{preset.description}</p>}
                    {preset.schemaGenerated && (
                      <p className="text-xs font-medium text-[hsl(var(--primary))]">Schema auto-generated from sample report</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!preset.isDefault && (
                      <button
                        type="button"
                        onClick={() => handleSetDefault(preset)}
                        className="btn-glass btn-secondary focus-ring text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={setDefaultMutation.isPending}
                      >
                        {setDefaultMutation.isPending && defaultPresetId !== preset._id ? 'Setting…' : 'Set as default'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDelete(preset)}
                      className="btn-glass btn-secondary focus-ring text-xs font-semibold text-[hsl(var(--destructive))] disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={deleteMutation.isPending}
                    >
                      Delete
                    </button>
                  </div>
                </header>
                {preset.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {preset.tags.map((tag) => (
                      <span key={tag} className="rounded-full bg-white/40 px-2 py-1 dark:bg-white/10">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
                <section className="rounded-xl border border-white/15 bg-white/40 p-3 dark:bg-white/10">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">Schema</p>
                  <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words text-xs font-mono text-muted-foreground">
                    {formatSchema(preset.schema)}
                  </pre>
                </section>
                {preset.versions.length > 1 && (
                  <section className="rounded-xl border border-dashed border-white/20 p-3 text-xs text-muted-foreground dark:border-white/10">
                    <p className="font-semibold text-foreground">Version history</p>
                    <div className="mt-2 space-y-1">
                      {preset.versions.map((version) => (
                        <Fragment key={version.version}>
                          <p>
                            v{version.version} · {version.createdAt ? new Date(version.createdAt).toLocaleString() : 'unknown'}
                          </p>
                        </Fragment>
                      ))}
                    </div>
                  </section>
                )}
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
